import React, { FC, useMemo } from 'react';
import { Avatar, AvatarGroup, Box, Skeleton, Typography } from '@mui/material';
import { FileVariantsEnum } from '@sweatpals/shared';
import { clsx, Colors, ProBadgeCrown, Showable, useStyles } from '@sweatpals/react-ui-kit';
import Link from 'next/link';
import RemoteImage from '@/modules/_core/components/images/Remote-image';
import useLink from '@/modules/_core/hooks/useLink';
import { eventCommunityStyles } from './event-community.styles';
import { UserDataType } from '@/modules/_core/types/user-types';
import { JoinButton } from '@/modules/host/components/join-button.component';

interface EventCommunityProps {
	community?: UserDataType;
	showSkeleton?: boolean;
	onHostLinkClick?: (userName: string) => void;
}

const EventCommunity: FC<EventCommunityProps> = ({ community, showSkeleton, onHostLinkClick }) => {
	const { makeHostLink } = useLink();
	const styles = useStyles(eventCommunityStyles);

	const communityPageUrl = useMemo(
		() => makeHostLink({ userName: community?.userName }),
		[community?.userName, makeHostLink]
	);

	if (!showSkeleton && !community) {
		return null;
	}

	const followers = community?.followers?.slice(0, 3) ?? [];

	return (
		<Box sx={styles.community}>
			<Showable show={showSkeleton}>
				<Skeleton animation="wave" sx={clsx(styles.communityName, { backgroundColor: Colors.white_a10 })} variant={'text'}/>
				<Box mt={'16px'} gap={'16px'} display={'flex'} flexDirection={{ xs: 'column', sm: 'row' }}>
					<Skeleton animation="wave" variant={'circular'} sx={{ width: 74, height: 74, backgroundColor: Colors.white_a10 }} />
					<Box display={'flex'} flex={1} flexDirection={'column'} gap={'16px'}>
						<Skeleton animation="wave" sx={clsx(styles.communityDescription, { backgroundColor: Colors.white_a10 })} variant={'text'}/>
						<Box display={'flex'} gap={'24px'} flexDirection={{ xs: 'column', sm: 'row' }}>
							<Skeleton animation="wave" sx={{ width: 172, height: 48, borderRadius: '100px', backgroundColor: Colors.white_a10 }} variant={'rounded'}/>
							<Skeleton animation="wave" sx={{ width: 162, height: 48, backgroundColor: Colors.white_a10 }} variant={'rounded'} />
						</Box>
					</Box>
				</Box>
			</Showable>
			<Showable show={community && !showSkeleton}>
				{() => (
					<>
						<Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
							<Typography sx={styles.communityName}>{community.fullName}</Typography>
							<Link href={communityPageUrl} passHref legacyBehavior>
								<Box sx={styles.link} onClick={() => onHostLinkClick?.(community?.userName)}>Learn More</Box>
							</Link>
						</Box>

						<Box mt={'16px'} gap={'16px'} display={'flex'} flexDirection={{ xs: 'column', sm: 'row' }}>
							<Box sx={{ position: 'relative', width: '74px', height: '74px' }}>
							<RemoteImage
								src={community.avatarId}
								alt={community.fullName}
								width={74}
								height={74}
								sx={{ borderRadius: '50%', flexShrink: 0 }}
								variant={FileVariantsEnum.S}
							/>
								<Showable show={community.isProPlanActive}>
									<Box sx={{ position: 'absolute', right: '-5px', bottom: '-5px' }}>
										<ProBadgeCrown size="large" />
									</Box>
								</Showable>
							</Box>
							<Box display={'flex'} flex={1} flexDirection={'column'} gap={'16px'}>
								<Showable show={community.myBio}>
									<Typography sx={styles.communityDescription}>
										{community.myBio}
									</Typography>
								</Showable>
								<Box
									display={'flex'}
									gap={'24px'}
									flexDirection={{ xs: 'column', sm: 'row' }}
									sx={{ flexWrap: 'wrap' }}
								>
									<JoinButton sx={styles.button} username={community.userName} />

									<Box display={'flex'} alignItems={'center'} gap={'8px'} sx={{ flexWrap: 'wrap' }}>
										<AvatarGroup variant="circular" sx={styles.communityAvatarGroup}>
											{followers.map(({ follower }) => (
												<Avatar key={follower.id} alt={follower.fullName} title={follower.fullName}>
													<RemoteImage
														alt={follower.fullName}
														src={follower.avatarId}
														width={56}
														height={56}
														variant={FileVariantsEnum.S}
													/>
												</Avatar>
											))}
										</AvatarGroup>
										{community.followersCount ? (
											<Typography sx={styles.communityFollowers}>
												{community.followersCount} member{community.followersCount > 1 ? 's' : ''}
											</Typography>
										) : null}
									</Box>
								</Box>
							</Box>
						</Box>
					</>
				)}
			</Showable>
		</Box>
	);
};

export default EventCommunity;
